<template>
  <div>
    <!-- Card header -->
    <div
      class="flex items-center justify-between border-b border-neutral-300 bg-neutral-200 px-6 py-4"
    >
      <h2 class="text-navy-800 flex items-center gap-2 text-sm font-semibold">
        <i class="pi pi-users text-navy-400" />
        Participants
        <span
          class="bg-navy-50 text-navy-500 inline-flex h-[22px] min-w-[22px] items-center justify-center rounded-full px-1.5 text-xs font-semibold"
        >
          {{ participants.length }}
        </span>
      </h2>
      <div class="flex items-center gap-1">
        <button
          v-if="isImported && isDraft"
          class="text-navy-500 hover:bg-navy-50 hover:text-navy-700 inline-flex cursor-pointer items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-semibold transition-colors"
          @click="$emit('editImported')"
        >
          <i class="pi pi-pen-to-square text-xs" />
          Edit
        </button>
        <button
          v-tooltip.top="!isDraft ? 'Only available in draft' : ''"
          :disabled="!isDraft"
          class="text-navy-500 hover:bg-navy-50 hover:text-navy-700 inline-flex cursor-pointer items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-50"
          @click="openAddModal()"
        >
          <i class="pi pi-plus text-xs" />
          Add
        </button>
      </div>
    </div>

    <!-- Participant rows -->
    <div v-if="participants.length > 0">
      <div
        v-for="participant in participants"
        :key="participant.id"
        class="flex items-center gap-3 border-b border-neutral-100 px-6 py-3 transition-colors last:border-b-0 hover:bg-neutral-50"
      >
        <span
          class="bg-navy-100 text-navy-600 flex h-[30px] w-[30px] shrink-0 items-center justify-center rounded-full text-[11px] font-bold tracking-wide"
        >
          {{ participantInitials(participant.name) }}
        </span>
        <div class="min-w-0 flex-1">
          <span class="text-navy-800 block truncate text-[13.5px] font-medium">
            {{ participant.name }}
            <i
              v-if="participant.source === 'imported'"
              v-tooltip.top="'Imported — name is read-only'"
              class="pi pi-lock ml-1 text-xs text-neutral-400"
            />
          </span>
          <StatusBadge
            v-if="order.verificationEnabled"
            :variant="verificationBadge(participant).variant"
            class="mt-1"
          >
            {{ verificationBadge(participant).label }}
          </StatusBadge>
          <StatusBadge
            v-if="
              order.documentEmailEnabled &&
              participant.portalCaptureStatus &&
              participant.portalCaptureStatus !== 'not_requested'
            "
            :variant="portalCaptureBadge(participant).variant"
            class="mt-1"
          >
            {{ portalCaptureBadge(participant).label }}
          </StatusBadge>
        </div>
        <button
          v-if="
            isDraft ||
            hasReviewableVerification(participant) ||
            hasReissueablePortalAccess(participant) ||
            hasRevokeablePortalAccess(participant)
          "
          class="text-navy-400 hover:bg-navy-100 hover:text-navy-700 cursor-pointer rounded-md p-1.5 transition-colors"
          @click="(event) => toggleMenu(event, participant)"
        >
          <i class="pi pi-ellipsis-v text-sm"></i>
        </button>
      </div>
      <Menu
        ref="menuRef"
        :model="getMenuItems(selectedParticipant)"
        :popup="true"
      />
    </div>

    <!-- Empty state -->
    <div
      v-else
      class="flex min-h-[120px] flex-col items-center justify-center p-8 text-center"
    >
      <p class="text-navy-600 text-sm font-medium">No participants yet</p>
      <p class="text-navy-400 mt-1 text-xs">Add participants to this order</p>
    </div>

    <!-- Add Participant Modal -->
    <Dialog
      v-model:visible="showAddModal"
      modal
      header="Add New Participant"
      :style="{ width: '32rem' }"
    >
      <form class="space-y-6" @submit.prevent="submitAdd">
        <AppInput
          v-model="addName"
          label="Name"
          placeholder="Enter participant name"
          :error="addError"
          required
        />
        <div class="mt-6 flex justify-end gap-3">
          <AppButton type="button" variant="secondary" @click="closeAddModal">
            Cancel
          </AppButton>
          <AppButton type="submit" :loading="isAdding">
            Add Participant
          </AppButton>
        </div>
      </form>
    </Dialog>

    <!-- Edit Participant Modal -->
    <Dialog
      v-model:visible="showEditModal"
      modal
      header="Edit Participant"
      :style="{ width: '32rem' }"
    >
      <form class="space-y-6" @submit.prevent="submitEdit">
        <AppInput
          v-model="editName"
          label="Name"
          placeholder="Enter participant name"
          :error="editError"
          required
        />
        <div class="mt-6 flex justify-end gap-3">
          <AppButton type="button" variant="secondary" @click="closeEditModal">
            Cancel
          </AppButton>
          <AppButton type="submit" :loading="isEditing">
            Update Participant
          </AppButton>
        </div>
      </form>
    </Dialog>

    <!-- Re-issue Confirm Dialog -->
    <Dialog
      v-model:visible="showReissueConfirm"
      modal
      header="Re-issue Portal Access"
      :style="{ width: '28rem' }"
    >
      <p class="text-navy-600 mb-6 text-sm">
        Does <strong>{{ reissueTarget?.name }}</strong> still have their access
        code, or do they need a new one?
      </p>
      <div class="flex flex-col gap-2">
        <AppButton @click="void performReissue(false)">
          Resend email — they still have their code
        </AppButton>
        <AppButton variant="secondary" @click="void performReissue(true)">
          Reset code — generate a new one
        </AppButton>
      </div>
    </Dialog>

    <!-- Re-issue PIN Dialog -->
    <Dialog
      v-model:visible="showPinDialog"
      modal
      header="Portal Access Re-issued"
      :style="{ width: '28rem' }"
      :closable="true"
    >
      <p class="text-navy-600 mb-4 text-sm">
        A new access email has been sent to
        <strong>{{ reissuedParticipantName }}</strong
        >. Give them the access code below — it is not included in the email.
      </p>
      <div
        class="bg-navy-50 border-navy-100 rounded-lg border px-6 py-4 text-center"
      >
        <p
          class="text-navy-400 mb-1 text-xs font-semibold tracking-wide uppercase"
        >
          Access Code
        </p>
        <p class="text-navy-900 text-4xl font-bold tracking-widest">
          {{ reissuedPin }}
        </p>
      </div>
      <div class="mt-6 flex justify-end">
        <AppButton @click="dismissPinDialog">Done</AppButton>
      </div>
    </Dialog>

    <!-- Verification Review Modal -->
    <VerificationReviewModal
      v-model:visible="showVerificationModal"
      :participant-name="verificationParticipant?.name ?? ''"
      :order-id="order.gid"
      :participant-id="verificationParticipant?.id ?? null"
    />
  </div>
</template>

<script setup lang="ts">
import { router } from "@inertiajs/vue3"
import Dialog from "primevue/dialog"
import Menu from "primevue/menu"
import { useConfirm } from "primevue/useconfirm"
import { useToast } from "primevue/usetoast"
import { computed, ref } from "vue"

import type { OrderType, ParticipantType } from "../../pages/Order/types"
import { apolloClient } from "../../queries/client"
import CREATE_PARTICIPANT from "../../queries/participant/create.gql"
import DESTROY_PARTICIPANT from "../../queries/participant/destroy.gql"
import REISSUE_PORTAL_ACCESS from "../../queries/participant/reissue_portal_access.gql"
import REVOKE_PORTAL_ACCESS from "../../queries/participant/revoke_portal_access.gql"
import UPDATE_PARTICIPANT from "../../queries/participant/update.gql"
import AppButton from "../ui/AppButton.vue"
import AppInput from "../ui/AppInput.vue"
import StatusBadge from "../ui/StatusBadge.vue"
import VerificationReviewModal from "./VerificationReviewModal.vue"

const toast = useToast()
const confirm = useConfirm()

const props = defineProps<{
  order: OrderType
  participants: ParticipantType[]
}>()

const isDraft = computed(() => props.order.status === "DRAFT")
const isImported = computed(() => !!props.order.importAgentRegistration)

defineEmits<{
  editImported: []
}>()

type BadgeVariant =
  | "success"
  | "warning"
  | "error"
  | "info"
  | "neutral"
  | "purple"
  | "blue"

const verificationBadge = (
  participant: ParticipantType,
): { label: string; variant: BadgeVariant } => {
  const verifications = participant.participantVerifications ?? []
  if (verifications.length === 0) {
    return { label: "Not Verified", variant: "neutral" }
  }
  const latest = verifications[verifications.length - 1]
  const map: Record<string, { label: string; variant: BadgeVariant }> = {
    APPROVED: { label: "Verified", variant: "success" },
    SUBMITTED: { label: "Submitted", variant: "info" },
    PENDING: { label: "Pending", variant: "warning" },
    REJECTED: { label: "Rejected", variant: "error" },
  }
  return map[latest.status] ?? { label: "Not Verified", variant: "neutral" }
}

const participantInitials = (name: string): string =>
  name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .slice(0, 2)
    .toUpperCase()

const portalCaptureBadge = (
  participant: ParticipantType,
): { label: string; variant: BadgeVariant } => {
  const map: Record<string, { label: string; variant: BadgeVariant }> = {
    issued: { label: "Email Sent", variant: "success" },
    pending: { label: "Email Captured", variant: "info" },
    declined: { label: "Email Declined", variant: "neutral" },
    not_requested: { label: "Email Not Requested", variant: "neutral" },
  }
  return (
    map[participant.portalCaptureStatus ?? "not_requested"] ?? {
      label: "Email Not Requested",
      variant: "neutral",
    }
  )
}

// ─── Verification Review ────────────────────────────────────────

const showVerificationModal = ref(false)
const verificationParticipant = ref<ParticipantType | null>(null)

const reviewableStatuses = ["SUBMITTED", "APPROVED", "REJECTED"]

const hasReviewableVerification = (participant: ParticipantType): boolean => {
  if (!props.order.verificationEnabled) return false
  const verifications = participant.participantVerifications ?? []
  if (verifications.length === 0) return false
  return reviewableStatuses.includes(
    verifications[verifications.length - 1].status,
  )
}

const openVerificationModal = (participant: ParticipantType) => {
  verificationParticipant.value = participant
  showVerificationModal.value = true
}

// ─── Portal Re-issue ─────────────────────────────────────────────

const isReissuing = ref(false)
const showPinDialog = ref(false)
const reissuedPin = ref<string | null>(null)
const reissuedParticipantName = ref("")
const showReissueConfirm = ref(false)
const reissueTarget = ref<ParticipantType | null>(null)

const hasReissueablePortalAccess = (participant: ParticipantType): boolean => {
  if (!props.order.documentEmailEnabled) return false
  return (
    participant.portalCaptureStatus === "issued" ||
    participant.portalCaptureStatus === "pending"
  )
}

const hasRevokeablePortalAccess = (participant: ParticipantType): boolean => {
  if (!props.order.documentEmailEnabled) return false
  return participant.portalCaptureStatus === "issued"
}

const dismissPinDialog = () => {
  showPinDialog.value = false
  router.reload()
}

const openReissueConfirm = (participant: ParticipantType) => {
  reissueTarget.value = participant
  showReissueConfirm.value = true
}

const performReissue = async (resetPin: boolean) => {
  const participant = reissueTarget.value
  if (!participant || isReissuing.value) return

  showReissueConfirm.value = false
  isReissuing.value = true

  try {
    const result = await apolloClient.mutate({
      mutation: REISSUE_PORTAL_ACCESS,
      variables: { participantId: participant.id, resetPin },
    })

    const errors = result?.data?.reissueParticipantPortalAccess?.errors ?? []
    if (errors.length) {
      toast.add({
        severity: "error",
        summary: "Error",
        detail: errors.join(", "),
        life: 5000,
      })
      return
    }

    const rawPin = result?.data?.reissueParticipantPortalAccess?.rawPin ?? null
    if (rawPin) {
      reissuedPin.value = rawPin
      reissuedParticipantName.value = participant.name
      showPinDialog.value = true
      // reload happens when the user dismisses the PIN dialog
    } else {
      toast.add({
        severity: "success",
        summary: "Access re-issued",
        detail: `A new access email has been sent to ${participant.name}`,
        life: 4000,
      })
      router.reload()
    }
  } catch {
    toast.add({
      severity: "error",
      summary: "Error",
      detail: "Failed to re-issue portal access",
      life: 5000,
    })
  } finally {
    isReissuing.value = false
  }
}

const revokePortalAccess = (participant: ParticipantType) => {
  confirm.require({
    message: `This will immediately cancel ${participant.name}'s portal access link. They will not be able to view documents until a new link is sent.`,
    header: "Revoke Portal Access",
    icon: "pi pi-exclamation-triangle",
    acceptClass: "p-button-danger",
    acceptLabel: "Revoke Access",
    rejectLabel: "Cancel",
    accept: () => {
      void performRevoke(participant)
    },
  })
}

const performRevoke = async (participant: ParticipantType) => {
  try {
    const result = await apolloClient.mutate({
      mutation: REVOKE_PORTAL_ACCESS,
      variables: { participantId: participant.id },
    })
    const errors = result?.data?.revokeParticipantPortalAccess?.errors ?? []
    if (errors.length) {
      toast.add({
        severity: "error",
        summary: "Error",
        detail: errors.join(", "),
        life: 5000,
      })
      return
    }
    toast.add({
      severity: "success",
      summary: "Access revoked",
      detail: `Portal access for ${participant.name} has been revoked`,
      life: 4000,
    })
    router.reload()
  } catch {
    toast.add({
      severity: "error",
      summary: "Error",
      detail: "Failed to revoke portal access",
      life: 5000,
    })
  }
}

// ─── Kebab Menu ─────────────────────────────────────────────────

const menuRef = ref()
const selectedParticipant = ref<ParticipantType | null>(null)

const toggleMenu = (event: Event, participant: ParticipantType) => {
  selectedParticipant.value = participant
  menuRef.value.toggle(event)
}

const getMenuItems = (participant: ParticipantType | null) => {
  if (!participant) return []

  const items: object[] = []

  if (hasReviewableVerification(participant)) {
    items.push({
      label: "View",
      icon: "pi pi-eye",
      command: () => openVerificationModal(participant),
    })
  }

  if (hasReissueablePortalAccess(participant)) {
    items.push({
      label: "Re-issue portal access",
      icon: "pi pi-envelope",
      command: () => openReissueConfirm(participant),
    })
  }

  if (hasRevokeablePortalAccess(participant)) {
    items.push({
      label: "Revoke portal access",
      icon: "pi pi-ban",
      class: "text-red-600",
      command: () => revokePortalAccess(participant),
    })
  }

  if (isDraft.value) {
    if (participant.source !== "imported") {
      items.push({
        label: "Edit",
        icon: "pi pi-pen-to-square",
        command: () => openEditModal(participant),
      })
    }
    items.push({
      label: "Delete",
      icon: "pi pi-trash",
      class: "text-red-600",
      command: () => confirmDelete(participant),
    })
  }

  return items
}

// ─── Add Modal ───────────────────────────────────────────────────

const showAddModal = ref(false)
const addName = ref("")
const addError = ref<string | undefined>()
const isAdding = ref(false)

const openAddModal = () => {
  showAddModal.value = true
}

const closeAddModal = () => {
  showAddModal.value = false
  addName.value = ""
  addError.value = undefined
}

const submitAdd = async () => {
  if (!addName.value.trim()) {
    addError.value = "Name is required"
    return
  }

  isAdding.value = true
  addError.value = undefined

  try {
    const result = await apolloClient.mutate({
      mutation: CREATE_PARTICIPANT,
      variables: {
        orderId: props.order.gid,
        name: addName.value,
      },
    })

    if (result?.data?.createParticipant?.errors?.length) {
      addError.value = result.data.createParticipant.errors.join(", ")
      return
    }

    toast.add({
      severity: "success",
      summary: "Success",
      detail: "Participant added successfully",
      life: 4000,
    })

    closeAddModal()
    router.reload()
  } catch (error) {
    console.error("Mutation error:", error)
    toast.add({
      severity: "error",
      summary: "Error",
      detail: "Failed to add participant",
      life: 5000,
    })
  } finally {
    isAdding.value = false
  }
}

// ─── Edit Modal ──────────────────────────────────────────────────

const showEditModal = ref(false)
const editingId = ref<string | null>(null)
const editName = ref("")
const editError = ref<string | undefined>()
const isEditing = ref(false)

const openEditModal = (participant: ParticipantType) => {
  editingId.value = participant.id
  editName.value = participant.name
  showEditModal.value = true
}

const closeEditModal = () => {
  showEditModal.value = false
  editingId.value = null
  editName.value = ""
  editError.value = undefined
}

const submitEdit = async () => {
  if (!editingId.value) return

  if (!editName.value.trim()) {
    editError.value = "Name is required"
    return
  }

  isEditing.value = true
  editError.value = undefined

  try {
    const result = await apolloClient.mutate({
      mutation: UPDATE_PARTICIPANT,
      variables: {
        participantId: editingId.value,
        name: editName.value,
      },
    })

    if (result?.data?.updateParticipant?.errors?.length) {
      editError.value = result.data.updateParticipant.errors.join(", ")
      return
    }

    toast.add({
      severity: "success",
      summary: "Success",
      detail: "Participant updated successfully",
      life: 4000,
    })

    closeEditModal()
    router.reload()
  } catch (error) {
    console.error("Mutation error:", error)
    toast.add({
      severity: "error",
      summary: "Error",
      detail: "Failed to update participant",
      life: 5000,
    })
  } finally {
    isEditing.value = false
  }
}

// ─── Delete ──────────────────────────────────────────────────────

const confirmDelete = (participant: ParticipantType) => {
  confirm.require({
    message: "Are you sure you want to delete this participant?",
    header: "Confirm Delete",
    icon: "pi pi-exclamation-triangle",
    acceptClass: "p-button-danger",
    accept: () => {
      void deleteParticipant(participant)
    },
  })
}

const deleteParticipant = async (participant: ParticipantType) => {
  try {
    const result = await apolloClient.mutate({
      mutation: DESTROY_PARTICIPANT,
      variables: {
        participantId: participant.id,
      },
    })

    if (result?.data?.destroyParticipant?.success) {
      toast.add({
        severity: "success",
        summary: "Success",
        detail: "Participant deleted",
        life: 4000,
      })
      router.reload()
    } else {
      const errors = result?.data?.destroyParticipant?.errors || [
        "Unknown error",
      ]
      toast.add({
        severity: "error",
        summary: "Error",
        detail: errors.join(", "),
        life: 5000,
      })
    }
  } catch (error) {
    console.error("Mutation error:", error)
    toast.add({
      severity: "error",
      summary: "Error",
      detail: "Failed to delete participant",
      life: 5000,
    })
  }
}

// Expose for parent to trigger add modal
defineExpose({
  openAddModal,
})
</script>
